import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import type { Carrier, CarrierWarehouse } from '@magimp/shared';
import { Button, Input, Textarea } from '../../../ui/index.js';
import {
  createCarrierWarehouse,
  deleteCarrierWarehouse,
  patchCarrierWarehouse,
  type CarrierWarehouseBody,
} from '../../../api/requests/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { Card, CardRow, FieldGrid, Labeled, SectionTitle } from './CarrierEditorModal.parts.js';

const EMPTY_WAREHOUSE_DRAFT: CarrierWarehouseBody = { name: '', city: '', description: '' };

export function CarrierWarehouseSection({
  carrier,
  onRefresh,
  onDirtyChange,
}: {
  carrier: Carrier;
  onRefresh: () => Promise<void>;
  // INVARIANT: narrow primitive bubble-up only — never expose form refs or
  // mutation handles to the parent. Aggregating dirty into the parent's
  // single `useUnsavedChangesGuard` keeps the prompt count at one.
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation(['carriers', 'common']);
  const showToast = useToastStore((s) => s.show);
  const [draft, setDraft] = useState<CarrierWarehouseBody>(EMPTY_WAREHOUSE_DRAFT);
  const [rowDirtyIds, setRowDirtyIds] = useState<ReadonlySet<string>>(() => new Set());
  const create = useMutation({
    mutationFn: () => createCarrierWarehouse(carrier.id, draft),
    onSuccess: async () => {
      // Reset the draft baseline FIRST so `useEffect` below recomputes
      // dirty=false before the parent re-renders with the refreshed
      // carrier list (which would otherwise briefly mount the next row
      // with a stale dirty flag).
      setDraft(EMPTY_WAREHOUSE_DRAFT);
      await onRefresh();
      showToast({ tone: 'success', text: t('toasts.warehouseCreated') });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const draftDirty = isWarehouseDraftDirty(draft);
  const dirty = draftDirty || rowDirtyIds.size > 0;
  // WHY: keep `onDirtyChange` in a ref so the dirty-effect can depend
  // ONLY on `dirty`, and the unmount cleanup runs once on actual
  // unmount — not on every parent re-render that swaps the callback.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);
  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);
  useEffect(
    () => () => {
      onDirtyChangeRef.current?.(false);
    },
    [],
  );

  const setRowDirty = (id: string, isDirty: boolean): void => {
    setRowDirtyIds((prev) => {
      const has = prev.has(id);
      if (isDirty === has) return prev;
      const next = new Set(prev);
      if (isDirty) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const canCreate =
    draft.name.trim().length > 0 &&
    draft.city.trim().length > 0 &&
    draft.description.trim().length > 0 &&
    !create.isPending;

  return (
    <section>
      <SectionTitle>{t('sections.warehouses')}</SectionTitle>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {carrier.warehouses.map((row) => (
          <WarehouseRow
            key={row.id}
            carrierId={carrier.id}
            row={row}
            onRefresh={onRefresh}
            onDirtyChange={(d) => setRowDirty(row.id, d)}
          />
        ))}
        <Card>
          <FieldGrid>
            <Labeled label={t('fields.warehouseName')} required>
              <Input
                value={draft.name}
                onChange={(name) => setDraft({ ...draft, name })}
                placeholder={t('fields.warehouseName')}
              />
            </Labeled>
            <Labeled label={t('fields.city')} required>
              <Input
                value={draft.city}
                onChange={(city) => setDraft({ ...draft, city })}
                placeholder={t('fields.city')}
              />
            </Labeled>
          </FieldGrid>
          <Labeled label={t('fields.description')} required>
            <Textarea
              value={draft.description}
              onChange={(description) => setDraft({ ...draft, description })}
              placeholder={t('fields.description')}
            />
          </Labeled>
          <CardRow>
            <Button icon="plus" disabled={!canCreate} onClick={() => create.mutate()}>
              {t('actions.addWarehouse')}
            </Button>
          </CardRow>
        </Card>
      </div>
    </section>
  );
}

function isWarehouseDraftDirty(draft: CarrierWarehouseBody): boolean {
  return (
    draft.name.trim().length > 0 ||
    draft.city.trim().length > 0 ||
    draft.description.trim().length > 0
  );
}

function WarehouseRow({
  carrierId,
  row,
  onRefresh,
  onDirtyChange,
}: {
  carrierId: string;
  row: CarrierWarehouse;
  onRefresh: () => Promise<void>;
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const { t } = useTranslation(['carriers', 'common']);
  const showToast = useToastStore((s) => s.show);
  // INVARIANT: baseline is keyed on the latest `row.*` values; after a
  // successful save → onRefresh, the parent re-renders us with new props
  // and the useEffect below resyncs `form` so dirty drops to false.
  const [form, setForm] = useState<CarrierWarehouseBody>({
    name: row.name,
    city: row.city,
    description: row.description,
  });
  useEffect(() => {
    setForm({ name: row.name, city: row.city, description: row.description });
  }, [row.name, row.city, row.description]);

  const save = useMutation({
    mutationFn: () => patchCarrierWarehouse(carrierId, row.id, form),
    onSuccess: async () => {
      await onRefresh();
      showToast({ tone: 'success', text: t('toasts.warehouseUpdated') });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });
  const remove = useMutation({
    mutationFn: () => deleteCarrierWarehouse(carrierId, row.id),
    onSuccess: async () => {
      await onRefresh();
      showToast({ tone: 'success', text: t('toasts.warehouseDeleted') });
    },
    onError: (err) => showToast({ tone: 'error', text: errorMessage(err) }),
  });

  const dirty =
    form.name !== row.name || form.city !== row.city || form.description !== row.description;
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => {
    onDirtyChangeRef.current = onDirtyChange;
  }, [onDirtyChange]);
  useEffect(() => {
    onDirtyChangeRef.current?.(dirty);
  }, [dirty]);
  useEffect(
    () => () => {
      onDirtyChangeRef.current?.(false);
    },
    [],
  );

  const canSave =
    form.name.trim().length > 0 &&
    form.city.trim().length > 0 &&
    form.description.trim().length > 0 &&
    !save.isPending;

  return (
    <Card>
      <FieldGrid>
        <Labeled label={t('fields.warehouseName')} required>
          <Input value={form.name} onChange={(name) => setForm({ ...form, name })} />
        </Labeled>
        <Labeled label={t('fields.city')} required>
          <Input value={form.city} onChange={(city) => setForm({ ...form, city })} />
        </Labeled>
      </FieldGrid>
      <Labeled label={t('fields.description')} required>
        <Textarea
          value={form.description}
          onChange={(description) => setForm({ ...form, description })}
        />
      </Labeled>
      <CardRow>
        <Button size="sm" kind="danger" onClick={() => remove.mutate()}>
          {t('common:actions.delete')}
        </Button>
        <Button size="sm" disabled={!canSave} onClick={() => save.mutate()}>
          {t('common:actions.save')}
        </Button>
      </CardRow>
    </Card>
  );
}
